import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';

import { MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { ContextMenuModule } from 'primeng/contextmenu';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { FieldsetModule } from 'primeng/fieldset';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TreeSelectModule } from 'primeng/treeselect';
import { TreeTableModule } from 'primeng/treetable';

import { firstValueFrom, forkJoin, Subscription } from 'rxjs';

import { SETTINGS, Settings } from '../../../../app.settings';

import { notifyAndTrackError } from '../../../../common/error';
import { successMsg, warningMsg } from '../../../../common/messages';

import { CanComponentDeactivate } from '../../../../components';

import { AppInsightsService } from '../../../../services/app-insights.service';

import {
  CatalogAdminService,
  ICatalog,
  ICatalogOrderItemType,
  ICatalogOrderItemTypeUpdateRequest,
  ICatalogUpdateRequest,
  IDictionaryEntry,
  ILocation,
  LocationAdminService
} from '../../../../services/insecta';

@Component({
  selector: 'tab-orderitemtype',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    BreadcrumbModule,
    ButtonModule,
    CardModule,
    CheckboxModule,
    ContextMenuModule,
    CommonModule,
    DatePickerModule,
    DialogModule,
    DividerModule,
    FloatLabelModule,
    FieldsetModule,
    FormsModule,
    InputNumberModule,
    InputTextModule,
    RouterModule,
    SelectModule,
    TableModule,
    TabsModule,
    TextareaModule,
    ToggleSwitchModule,
    TreeSelectModule,
    TreeTableModule
  ],

  templateUrl: './tab-orderitemtype.component.html'
})
export class TabOrderItemTypeComponent implements OnDestroy, OnInit, CanComponentDeactivate {
  dirtyConfirm$: ((leave: boolean) => void) | null = null;
  emptyGuid: string = `00000000-0000-0000-0000-000000000000`;

  catalog: ICatalog = <ICatalog>{
    id: this.emptyGuid
  };

  location: ILocation = <ILocation>{
    id: this.emptyGuid
  };

  pageVars: any = {
    isDirty: false,
    isEditing: false,
    isReady: false,
    pageId: `Admin:SiteCatalog`,
    showDirtyDlg: false
  };

  dlgVars: any = {
    substrates: <IDictionaryEntry[]>[],
    visible: false
  };

  @Input() currentUser: string = ``;
  @Input() catalogId: string = this.emptyGuid;
  @Output('isReady') isReady = new EventEmitter<boolean>();

  catalogOrderItemTypes: ICatalogOrderItemType[] = [];
  isAvailable: boolean = true;

  catalog$!: Subscription;
  catalogSettings: any = {
    leadTime: null,
    leadTimeMessage: null,
    siteCode: null,
    bartenderUrl: null,
    isEditing: false,
    isBartenderEditing: false
  };

  constructor(
    private appInsights: AppInsightsService,
    private catalogService: CatalogAdminService,
    private locationService: LocationAdminService,
    private msgService: MessageService,
    private activatedRoute: ActivatedRoute,
    @Inject(SETTINGS) private settings: Settings
  ) {
    this.pageVars.pageId = `${settings.environment}:Admin:SiteCatalog:OrderItemTypeTab`;
    appInsights.trackPageView(this.pageVars.pageId, `/admin/sitecatalog#taborderitemtypes`);
  }

  canDeactivate(): Promise<boolean> {
    if (!this.pageVars.isEditing) return Promise.resolve(true);

    return new Promise((resolve) => {
      this.pageVars.showDirtyDlg = true;
      const confirm$ = (decision: boolean) => {
        this.dirtyConfirm$ = null;
        resolve(decision);
      };
      this.dirtyConfirm$ = confirm$;
    });
  }

  dirtyConfirm(leave: boolean) {
    this.pageVars.showDirtyDlg = false;
    if (this.dirtyConfirm$) {
      this.dirtyConfirm$(leave);
    }
  }

  ngOnDestroy() {
    this.pageVars = {
      isReady: false
    };
  }

  ngOnInit() {
    this.isReady.emit(false);
    this.appInsights.trackEvent(`${this.pageVars.pageId}:Init`);
    this.pageVars.isReady = false;

    this.activatedRoute.paramMap.subscribe((params) => {
      this.location.id = <string>params.get('locationId') ?? this.emptyGuid;

      const request = {
        orderTypes: this.catalogService.getCatalogOrderItemType(this.catalogId),
        location: this.locationService.getLocation(this.location.id),
        catalog: this.catalogService.getCatalogByLocation(this.location.id)
      };

      firstValueFrom(forkJoin(request))
        .then((response) => {
          this.catalog = response.catalog;
          this.catalogSettings.siteCode = this.catalog.siteCode;
          this.catalogOrderItemTypes = response.orderTypes;
        })
        .catch((err) => {
          notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'TabOrderItemTypeComponent', action: 'Init:Error' });
        })
        .finally(() => {
          this.pageVars.isReady = true;
          this.pageVars.isEditing = false;
          this.isReady.emit(true);
        });
    });
  }

  onEditSiteCode() {
    this.catalogSettings.isEditing = true;
    this.catalogSettings.siteCode = this.catalog.siteCode;
  }

  onCancelSiteCode() {
    this.catalogSettings.isEditing = false;
    this.catalogSettings.siteCode = this.catalog.siteCode;
  }

  onSaveSiteCode() {
    const code = this.catalogSettings.siteCode?.trim().toUpperCase();
    if (!code) {
      warningMsg(this.msgService, 'Site Code is required');
      return;
    }
    if (code.length !== 2) {
      warningMsg(this.msgService, 'Site Code must be 2 characters');
      return;
    }

    const request: ICatalogUpdateRequest = {
      id: this.catalog.id,
      locationId: this.catalog.locationId,
      siteCode: code,
      actionBy: this.currentUser
    };

    firstValueFrom(this.catalogService.updateCatalog(request))
      .then(() => {
        this.catalog.siteCode = request.siteCode;
        this.catalog.action = 'UPD';
        this.catalog.actionBy = this.currentUser;
        this.catalog.actionOn = new Date(Date.now());
        this.catalogSettings.isEditing = false;
        successMsg(this.msgService, 'Site Code has been updated');
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'TabOrderItemTypeComponent', action: 'SiteCode:Update:Error' });
        if (err?.message?.includes('duplicate')) {
          notifyAndTrackError(this.msgService, this.appInsights, 'This Site Code already exists', {
            component: 'TabOrderItemTypeComponent',
            action: 'SiteCode:Duplicate'
          });
        }
      });
  }

  updateCatalogOrderItemType(catalogOrderItemType: ICatalogOrderItemType) {
    const request: ICatalogOrderItemTypeUpdateRequest = {
      id: catalogOrderItemType.id,
      leadTime: catalogOrderItemType.leadTime,
      hasRecurringOrders: catalogOrderItemType.hasRecurringOrders,
      maxRecurrenceDuration: catalogOrderItemType.maxRecurrenceDuration,
      isActive: catalogOrderItemType.isActive,
      actionBy: this.currentUser,
      action: catalogOrderItemType.action,
      actionOn: new Date(Date.now())
    };

    firstValueFrom(this.catalogService.updateCatalogOrderItemType(this.catalogId, request.id, request))
      .then(() => {
        catalogOrderItemType.isActive = request.isActive;
        catalogOrderItemType.leadTime = request.leadTime;
        catalogOrderItemType.hasRecurringOrders = request.hasRecurringOrders;
        catalogOrderItemType.maxRecurrenceDuration = request.maxRecurrenceDuration;
        catalogOrderItemType.action = request.action;
        catalogOrderItemType.actionBy = request.actionBy;
        catalogOrderItemType.actionOn = request.actionOn;
        successMsg(this.msgService, 'Catalog Order Item has been update');
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, {
          component: 'TabOrderItemTypeComponent',
          action: 'UpdateCatalogOrderItemType:Error'
        });
      });
  }

  dlgCancel() {
    this.dlgVars.visible = false;
    this.dlgVars.substrates = [];
    this.dlgVars.substrate = null;
  }
}
